import { useEffect, useState } from "react";
import { getCancellationPolicy } from "../api/services/rentalConditionService";
import { Check } from "lucide-react";

export const CancellationTermsModal = ({
  isOpen,
  onClose,
  searchId,
  carOfferHash,
  policies: externalPolicies,
}) => {
  const [loading, setLoading] = useState(false);
  const [policies, setPolicies] = useState([]);
  const [error, setError] = useState("");
  useEffect(() => {
    if (!isOpen) return;

    if (Array.isArray(externalPolicies)) {
      setLoading(false);
      setPolicies(externalPolicies);
      setError(
        externalPolicies.length
          ? ""
          : "No cancellation terms were found for this reservation.",
      );
    }
  }, [isOpen, externalPolicies]);

  useEffect(() => {
    if (!isOpen || Array.isArray(externalPolicies)) return;

    let cancelled = false;

    const loadCancellationPolicy = async () => {
      try {
        setLoading(true);
        setError("");
        setPolicies([]);

        const res = await getCancellationPolicy(searchId, carOfferHash);

        /**
         * Supports multiple possible response shapes:
         * 1. Axios response: res.data = [...]
         * 2. API wrapper response: res = [...]
         * 3. Nested response: res.data.data = [...]
         */
        const policyData = Array.isArray(res)
          ? res
          : Array.isArray(res?.data)
            ? res.data
            : Array.isArray(res?.data?.data)
              ? res.data.data
              : [];

        if (!cancelled) {
          setPolicies(policyData);

          if (policyData.length === 0) {
            setError("No cancellation terms were found for this offer.");
          }
        }
      } catch (err) {
        console.error("Cancellation policy fetch failed:", err);

        if (!cancelled) {
          setError(
            "We were unable to load the cancellation terms at the moment. Please try again.",
          );
        }
      } finally {
        if (!cancelled) {
          setLoading(false);
        }
      }
    };

    if (searchId && carOfferHash) {
      loadCancellationPolicy();
    } else {
      setPolicies([]);
      setError("Missing search or offer information.");
    }

    return () => {
      cancelled = true;
    };
  }, [isOpen, searchId, carOfferHash]);

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-[99999] flex items-center justify-center bg-black/20 backdrop-blur-sm p-2">
      <div className="bg-white rounded-2xl shadow-2xl w-full max-w-2xl border border-slate-100 overflow-hidden">
        {/* Header */}
        <div className="bg-white px-6 py-5 border-b border-slate-100">
          <h2 className="font-heading text-primary font-bold text-xl">
            Cancellation Terms
          </h2>
        </div>

        {/* Body */}
        <div className="p-6">
          {loading && (
            <div className="flex flex-col items-center justify-center py-10 gap-3 text-slate-400">
              <div className="w-8 h-8 border-4 border-slate-200 border-t-primary rounded-full animate-spin" />
              <p className="font-heading text-sm font-medium">Loading…</p>
            </div>
          )}

          {!loading && error && (
            <div className="rounded-xl border border-red-100 bg-red-50 p-4 text-sm text-red-600">
              {error}
            </div>
          )}

          {!loading && !error && policies.length > 0 && (
            <div className="space-y-3">
              {policies.map((policy, index) => (
                <div
                  key={index}
                  className="flex items-start gap-3 rounded-xl border border-primary bg-slate-80 p-4"
                >
                  <span className="mt-0.5 flex h-6 w-6 shrink-0 items-center justify-center rounded-full bg-primary/15 text-primary">
                    <Check size={14} />
                  </span>

                  <p className="font-body text-sm leading-6 text-slate-800">{policy}</p>
                </div>
              ))}
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="border-t border-slate-100 px-6 py-4 flex justify-end">
          <button
            type="button"
            onClick={onClose}
            className="bg-primary hover:bg-primary-hover text-white font-bold px-6 py-2.5 rounded-xl text-sm transition"
          >
            Close
          </button>
        </div>
      </div>
    </div>
  );
};
